import type { LiveSummary } from '@multimod/shared';
import { PlatformIcon } from '@/components/ui/platform-icon';
import { formatUptime } from '@/features/panel/lib/time';

function platformLabel(p: LiveSummary['platform']): string {
  return p === 'youtube' ? 'YouTube' : 'Twitch';
}

export function LiveCard({
  live,
  onSelect,
  onAttach,
}: {
  live: LiveSummary;
  onSelect: (live: LiveSummary) => void;
  onAttach?: (live: LiveSummary) => void;
}) {
  const platColor =
    live.platform === 'youtube' ? 'text-platform-youtube' : 'text-platform-twitch';
  return (
    <div className="flex w-full items-stretch gap-1">
      <button
        onClick={() => onSelect(live)}
        className="group flex flex-1 items-start gap-3 rounded-lg border border-border bg-bg-card p-2.5 text-left transition-colors hover:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary min-w-0"
      >
        <img
          src={live.thumbnailUrl}
          alt={`Thumbnail de ${live.displayName}`}
          // 120×68 ≈ 16:9 (spec C.3). Arbitrário pra match exato do design.
          className="rounded object-cover shrink-0 w-[120px] h-[68px] bg-bg-muted"
          loading="lazy"
          width={120}
          height={68}
        />
        <div className="min-w-0 flex-1 flex flex-col gap-0.5">
          <div className="flex items-center gap-1.5 min-w-0">
            <PlatformIcon
              platform={live.platform}
              size={12}
              className={`${platColor} shrink-0`}
            />
            <span className="truncate text-sm font-semibold tracking-[-0.005em] text-fg">
              {live.displayName}
            </span>
            <span className="sr-only">({platformLabel(live.platform)})</span>
          </div>
          {/* line-clamp-2 trunca em 2 linhas mantendo ellipsis nativo do browser */}
          <p className="line-clamp-2 text-xs text-fg-muted leading-snug">
            {live.title || '—'}
          </p>
          <p className="text-2xs text-fg-subtle tabular-nums mt-0.5">
            <span className="text-fg-muted font-medium">
              {live.viewerCount.toLocaleString('pt-BR')}
            </span>
            <span> viewers · há </span>
            <span className="text-fg-muted font-medium">{formatUptime(live.startedAt)}</span>
            {live.gameName && (
              <>
                <span> · </span>
                <span>{live.gameName}</span>
              </>
            )}
          </p>
        </div>
      </button>
      {onAttach && (
        <button
          onClick={() => onAttach(live)}
          aria-label={`Anexar live ${live.displayName} ao chat ativo`}
          className="flex min-h-[68px] min-w-[44px] items-center justify-center rounded-lg border border-border bg-bg-card px-3 text-sm font-medium text-fg-muted transition-colors hover:border-primary hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          Anexar
        </button>
      )}
    </div>
  );
}
